import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { articleService, productService } from 'services';
import useErrors from 'hooks/useErrors';
import { toast } from 'react-toastify';
import { formErrorMessages, getItemFromStore, setItemToStore } from 'helpers/utils';
import { useSearchParams } from 'react-router-dom';
import { create } from 'adapters/productAdacter';
import { ProductContext } from 'context/Context';
import { useContext } from 'react';
import { newProductForm } from 'schemas/product.schema';

const useProductHook = product => {
 const { setError } = useErrors();
 const [loading, setLoading] = useState(false);
 const [asyncProducts, setAsyncProducts] = useState(false);
 const { getProducts, addFavourite, removeFavourite } = productService.useApiProducts();

 const refs = product?.article?.references.map(e => ({ ...e.properties[0], ref: e.reference })) || [];

 const {
  productsState: { cartItems },
  productsDispatch,
  isInShoppingCart,
  isInFavouriteItems
 } = useContext(ProductContext);

 useQuery(['products', {}], getProducts, {
  onSuccess: data => {
   console.log({ products: data }, 'products');
   productsDispatch({ type: 'SET_PRODUCTS', payload: { products: data } });
  },
  onSettled: () => setLoading(false),
  onError: err => setError(err),
  enabled: asyncProducts
 });

 const setProducts = () => setAsyncProducts(true);

 const referencePrices = product?.pricingReference.map(e => ({
  ...e,
  references: product?.article?.references.reduce((acc, b) => {
   const refs = b.properties.filter(u => e.references.some(s => s === u._id)).map(i => i._id);
   if (refs.length) acc.push({ ref: b.reference, items: refs });
   return acc;
  }, [])
 }));

 const priceByReference = references => {
  const { regularPrice, price } = product;
  const newPrice = referencePrices.find(e =>
   e.references.every(a => a.items.some(u => references.some(a => a._id === u)))
  )?.price;

  return newPrice ?? (regularPrice || price);
 };

 const handleAddToCart = (quantity, showModal, add, references = refs) => {
  const price = priceByReference(references);
  if (isInShoppingCart(product?._id, references)) {
   const cartProduct = cartItems.find(item => item._id === product?._id);
   productsDispatch({
    type: 'UPDATE_CART_ITEM',
    payload: {
     product: {
      ...cartProduct,
      references,
      quantity: add ? cartProduct.quantity + quantity : quantity,
      totalPrice: quantity * price // product.price
     },
     showModal,
     quantity
    }
   });
  } else {
   productsDispatch({
    type: 'ADD_TO_CART',
    payload: {
     product: {
      ...product,
      references,
      quantity,
      totalPrice: quantity * price // product.price
     }
    }
   });
  }
 };

 const handleFavouriteClick = () => {
  const type = isInFavouriteItems(product._id) ? 'REMOVE_FROM_FAVOURITES' : 'ADD_TO_FAVOURITES';

  if (type === 'REMOVE_FROM_FAVOURITES') removeFavourite(product._id);
  else addFavourite({ product: product._id });

  productsDispatch({ type, payload: { product } });
 };

 return { setProducts, handleAddToCart, priceByReference, handleFavouriteClick, loading };
};

const useProductMutation = () => {
 const { setError } = useErrors();
 const [code, setCode] = useState(false);
 const [type, setType] = useState();
 const [loading, setLoading] = useState(false);
 const { createProduct } = productService.useApiProducts();
 const { getArticle, getArticleNames } = articleService.useApiArticles();
 const { mutate: newProduct } = useMutation(createProduct);
 const [articles, setArticles] = useState(getItemFromStore('articles_name', []));
 const [searchParams] = useSearchParams();

 const methods = useForm({
  resolver: yupResolver(newProductForm.schema),
  defaultValues: getItemFromStore('newProduct', newProductForm.defaultValues)
 });

 const { handleSubmit, reset, unregister, watch } = methods;

 useQuery(['articles_names', {}], getArticleNames, {
  onSuccess: data => {
   setArticles(data.map(e => ({ value: e.code, label: e.name })));
   setItemToStore('articles_name', data);
  },
  onSettled: () => setLoading(false),
  onError: err => setError(err),
  enabled: /product|recipe/.test(type)
 });

 const { data: article } = useQuery(['article', code, { createdAt: 0 }], getArticle, {
  onSuccess: data => setItemToStore('article', data),
  onSettled: () => setLoading(false),
  onError: err => setError(err),
  enabled: !!code && /product|recipe/.test(type)
 });

 const onError = e => {
  for (const prop in e) {
   toast.error(
    <>
     <h5 className="text-white">{prop}:</h5>
     <p className="mb-0">{e[prop].message}</p>
    </>
   );
  }
  console.log(e, 'este es el error que surje');
 };

 const onSubmit = async data => {
  console.log(data);
  const adacterData = await create(data);
  setLoading(true);
  newProduct(
   { code: null, data: adacterData },
   {
    onSuccess: () => {
     reset(newProductForm.defaultValues);
     localStorage.removeItem('newProduct');
    },
    onError: err => setError(err),
    onSettled: (data, error) => {
     setLoading(false);
     toast[error ? 'error' : 'success'](error ? 'Error al procesar la solicitud' : 'Producto creado correctamente');
    }
   }
  );
 };

 useEffect(() => {
  if (type) unregister(newProductForm.unregisterFields[type]);
 }, [type]);

 useEffect(() => {
  setCode(searchParams.get('code'));
  setType(searchParams.get('type'));
 }, [searchParams]);

 useEffect(() => {
  const subscription = watch((value, { name }) => {
   console.log(value, 'el formulario cambio', name);
   setItemToStore('newProduct', value);
  });
  return () => subscription.unsubscribe();
 }, [watch]);

 return { type, loading, methods, handleSubmit, reset, watch, onSubmit, onError, article, articles };
};

/* const useProductDetails = product => {
 const { setError } = useErrors();
 const [loading, setLoading] = useState(false);
 const { getProduct } = productService.useApiProducts();

 const { data } = useQuery(['productWithItem', product, {}], getProduct, {
  onSuccess: data => {
   setItemToStore('productWithItem', data);
   console.log({ products: data }, 'productWithItem');
  },
  onSettled: () => setLoading(false),
  onError: err => setError(err),
  enabled: true
 });

 return { loading, data };
}; */

const useProductRating = ({ product = null, ratings = false } = {}) => {
 const { setError } = useErrors();
 const [loading, setLoading] = useState(false);
 const { getRatings, createRating } = productService.useApiProducts();
 const { mutate: newRating } = useMutation(createRating);

 const queryClient = useQueryClient();

 const methods = useForm({
  resolver: yupResolver(newProductForm.schemaRating),
  defaultValues: getItemFromStore('rating', newProductForm.defaultValuesRating)
 });

 const { handleSubmit, reset, watch } = methods;

 const { data: reviews } = useQuery(['reviews', { product }], getRatings, {
  onSuccess: data => {
   console.log({ reviews: data }, 'reviews');
  },
  onSettled: () => setLoading(false),
  onError: err => setError(err),
  enabled: ratings
 });

 const onError = errors => {
  console.log(errors, 'este es el error que surje');
  toast.error(
   <>
    <h5 className="text-white">Errores:</h5>
    <code>{JSON.stringify(formErrorMessages(errors), null, 2)}</code>
   </>
  );
 };

 const onSubmit = async data => {
  data = product ? { ...data, product } : data;
  console.log(data);
  setLoading(true);
  newRating(data, {
   onSuccess: () => {
    reset(newProductForm.defaultValuesRating);
    localStorage.removeItem('rating');
    queryClient.invalidateQueries(['reviews']);
   },
   onError: err => setError(err),
   onSettled: (data, error) => {
    setLoading(false);
    toast[error ? 'error' : 'success'](error ? 'Error al procesar la solicitud' : 'Reseña creada correctamente');
   }
  });
 };

 useEffect(() => {
  const subscription = watch((value, { name }) => {
   console.log(value, 'el formulario cambio', name);
   setItemToStore('rating', value);
  });
  return () => subscription.unsubscribe();
 }, [watch]);

 return { reviews, loading, methods, handleSubmit, onSubmit, onError };
};

export { useProductHook, useProductMutation, useProductRating };
